import { useMemo, useState } from 'react'
import { List as ListIcon, Clear as ClearIcon, Edit as EditIcon } from '@mui/icons-material'
import { Button, CircularProgress, Fab, Tooltip } from '@mui/material'
import { ErrorCorrectionButton } from 'error-correction'
import { motion, AnimatePresence } from 'framer-motion'
import isEmpty from 'lodash/isEmpty'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useDatasetDataContext } from '@datasets/dataset-details/datasetData/context'
import { ActionType } from '@datasets/dataset-details/datasetData/context/types'
import { DuplicateRows } from './duplicateRows'
import { EditSelectRowsTooltip } from './editSelectRowsTooltip'
import { RemoveRows } from './removeRows'
import { ConfirmEditModal } from '../confirm-edit-modal'
import * as css from './styles'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'

interface Props {
  manualEditDisabled: boolean
  numSelectedRows: number
  isRollup?: boolean
  removingRows?: boolean
  manualEditRows: () => Promise<void>
  correctErroredRows?: () => Promise<void>
  removeRows?: () => Promise<void>
  duplicateRows?: () => void
  shouldShowEditConfirmModal: boolean
}

export const EditSelectRows = ({
  manualEditDisabled,
  numSelectedRows,
  isRollup = false,
  removingRows,
  manualEditRows,
  correctErroredRows,
  removeRows,
  duplicateRows,
  shouldShowEditConfirmModal,
}: Props) => {
  const {
    manualEditLoading,
    selectedRows,
    selectiveEditModeEnabled,
    dispatch: dispatchState,
  } = useDatasetDataContext()
  const location = useLocation()
  const [DELETE_FROM_ROLLUP_ENABLED = false, ERROR_CORRECTION_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DELETE_FROM_ROLLUP, FeatureDict.ERROR_CORRECTION]),
  )

  const [isConfirmEditModalOpen, setIsConfirmEditModalOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const editSelectRowsTitle = selectiveEditModeEnabled ? 'Cancel' : 'Edit select rows'
  const editSelectRowsIcon = selectiveEditModeEnabled ? (
    <ClearIcon key="tt-1" data-testid="clear-icon" />
  ) : (
    <ListIcon key="tt-2" data-testid="list-icon" />
  )
  const showRemoveRows = DELETE_FROM_ROLLUP_ENABLED && isRollup && removeRows
  const showDuplicateRows = isRollup && duplicateRows
  const editButtonText =
    numSelectedRows === 1 ? `Edit ${numSelectedRows} row` : `Edit ${numSelectedRows} rows`

  const selectedRowsKeys = Object.keys(selectedRows) || [1]
  const firstRowSelected = selectedRows && Number(selectedRowsKeys[0])
  const lastRowSelected = selectedRows && Number(selectedRowsKeys[selectedRowsKeys.length - 1])

  const isTemplateDetailsPage = location.pathname.includes('/template/')
  const showSelectiveEditActions = isTemplateDetailsPage
    ? !isEmpty(selectedRows)
    : selectiveEditModeEnabled

  const selectedRowsHaveErrors = useMemo(
    () =>
      Object.values(selectedRows).some(
        row => typeof row === 'object' && Object.values(row.data).some(cell => cell.errors),
      ),
    [selectedRows],
  )

  return (
    <>
      <AnimatePresence>
        <Tooltip
          title={editSelectRowsTitle}
          open={tooltipOpen}
          onOpen={handleOpenTooltip}
          onClose={handleCloseTooltip}
          key="animate-1"
          data-testid="select-rows-tooltip"
        >
          <motion.div>
            <Fab
              size="medium"
              disabled={manualEditLoading}
              css={[css.fab, css.purple]}
              onClick={selectiveEditModeEnabled ? handleClickCancel : handleEditIntent}
              data-testid="select-rows-fab"
            >
              {editSelectRowsIcon}
            </Fab>
          </motion.div>
        </Tooltip>

        {showSelectiveEditActions && (
          <>
            {showRemoveRows && (
              <RemoveRows
                numSelectedRows={numSelectedRows}
                removingRows={Boolean(removingRows)}
                removeRows={removeRows}
                key="animate-2"
              />
            )}
            {showDuplicateRows && (
              <DuplicateRows
                key="animate-3"
                numSelectedRows={numSelectedRows}
                duplicateRows={duplicateRows}
              />
            )}
            {ERROR_CORRECTION_ENABLED && !!correctErroredRows && (
              <ErrorCorrectionButton
                onCorrectErrors={correctErroredRows}
                disabled={manualEditDisabled || !selectedRowsHaveErrors}
                selectedRowCount={numSelectedRows}
              />
            )}
            <Tooltip
              title={
                <EditSelectRowsTooltip
                  selectedRows={selectedRows}
                  numSelectedRows={numSelectedRows || 0}
                  firstRowSelected={firstRowSelected}
                  lastRowSelected={lastRowSelected}
                />
              }
              key="animate-4"
              data-testid="select-rows-wrapper-tooltip"
            >
              <css.ButtonContainer>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => void manualEditRows()}
                  css={css.editButton}
                  disabled={manualEditDisabled || manualEditLoading}
                  component={motion.button}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  startIcon={<EditIcon />}
                  data-testid="select-rows-edit-button"
                >
                  {editButtonText}
                </Button>
                {manualEditLoading && (
                  <CircularProgress
                    size={20}
                    css={css.buttonProgress}
                    data-testid="circular-progress"
                  />
                )}
              </css.ButtonContainer>
            </Tooltip>
          </>
        )}
      </AnimatePresence>
      {shouldShowEditConfirmModal && (
        <ConfirmEditModal
          open={isConfirmEditModalOpen}
          onClose={() => {
            setIsConfirmEditModalOpen(false)
          }}
          onContinue={handleEdit}
        />
      )}
    </>
  )

  function handleEditIntent() {
    if (shouldShowEditConfirmModal) setIsConfirmEditModalOpen(true)
    else handleEdit()
  }

  function handleOpenTooltip(): void {
    setTooltipOpen(true)
  }

  function handleCloseTooltip(): void {
    setTooltipOpen(false)
  }

  function handleEdit(): void {
    setTooltipOpen(false)
    setIsConfirmEditModalOpen(false)
    dispatchState({ type: ActionType.editMode, payload: { isEnabled: !selectiveEditModeEnabled } })
  }

  function handleClickCancel(): void {
    setTooltipOpen(false)
    dispatchState({ type: ActionType.selectedRows, payload: { data: {} } })

    if (!isTemplateDetailsPage) {
      dispatchState({
        type: ActionType.editMode,
        payload: { isEnabled: !selectiveEditModeEnabled },
      })
    }
  }
}
